import { Module, VuexModule, Action, Mutation } from 'vuex-module-decorators';
import axios from 'axios';
import { toError, assertOk } from '@/services/normalize';
import type { ExtAxiosConfig } from '@/middleware/axios-ui';

import {
  BASE_URL,
  USERS_GENERATE,
  USERS_CREATE,
  USERS,
  USERS_SUBAGENT,
  USERS_ACTIVATE,
  RESET_PASSWORD,
  USERS_HISTORY,
  UPDATE_PERMISSION,
  GET_USERS_DOWNLINE,
  UPDATE_COMMISSION,
  CHECK_PREFIX,
  GET_PTSUBAGENT,
  UPDATE_SHOW_CURRENCY_SIGNS,
  USERS_SHEET,
  M_POST,
  M_PUT
} from '@/utils/api';

@Module({ name: 'account' })
export default class Account extends VuexModule {
  users: any = {};

  subAccount: any = {};

  history: any = {};

  @Mutation
  mutateDownline(users: any) {
    this.users = users;
  }

  @Mutation
  mutateDownlineSearch(users: any) {
    this.users = users;
  }

  @Mutation
  mutateSubAccount(data: any) {
    this.subAccount = data;
  }

  @Mutation
  mutateHistory(data: any) {
    this.history = data;
  }

  @Action({ rawError: true })
  async GenerateUser(user: Record<string, any>) {
    try {
      const { data } = await axios({
        url: BASE_URL + USERS_GENERATE,
        data: user,
        method: M_POST
      });

      assertOk(data, 'Generate user failed');
      return data;
    } catch (err) {
      throw toError(err, 'Generate user failed');
    }
  }

  @Action({ rawError: true })
  async CreateAccount(user: Record<string, any>) {
    try {
      const { data } = await axios({
        url: BASE_URL + USERS_CREATE,
        data: user,
        method: M_POST
      });

      assertOk(data, 'Create account failed');
      return data;
    } catch (err) {
      throw toError(err, 'Create account failed');
    }
  }

  @Action({ commit: 'mutateDownline', rawError: true })
  async FetchDownLine(user: Record<string, any>) {
    try {
      const { __successToast, ...payload } = user || {};

      const { data } = await axios({
        url: BASE_URL + USERS,
        data: payload,
        method: M_POST,
        __successToast
      } as ExtAxiosConfig);

      assertOk(data, 'Fetch downline failed');
      return { ...(data?.data ?? {}) };
    } catch (err) {
      throw toError(err, 'Fetch downline failed');
    }
  }

  @Action({ commit: 'mutateDownlineSearch', rawError: true })
  async FetchDownLineSearch(payload: any) {
    try {
      const { __successToast, ...criteria } = payload || {};

      const { data } = await axios({
        url: BASE_URL + GET_USERS_DOWNLINE,
        data: criteria,
        method: M_POST,
        __successToast
      } as ExtAxiosConfig);

      assertOk(data, 'Fetch downline search failed');
      return { ...(data?.data ?? {}) };
    } catch (err) {
      throw toError(err, 'Fetch downline search failed');
    }
  }

  @Action({ commit: 'mutateSubAccount', rawError: true })
  async FetchSubAccount(user: Record<string, any>) {
    try {
      const { data } = await axios({
        url: BASE_URL + USERS_SUBAGENT,
        data: user,
        method: M_POST
      });

      assertOk(data, 'Fetch sub account failed');
      return data?.data?.users;
    } catch (err) {
      throw toError(err, 'Fetch sub account failed');
    }
  }

  @Action({ rawError: true })
  async ActiveUser(user: Record<string, any>) {
    try {
      const { data } = await axios({
        url: BASE_URL + USERS_ACTIVATE,
        data: user,
        method: M_POST
      });

      return data?.status ?? data;
    } catch (err) {
      throw toError(err, 'Activate user failed');
    }
  }

  @Action({ rawError: true })
  async ResetPassword(user: Record<string, any>) {
    try {
      const { data } = await axios({
        url: BASE_URL + RESET_PASSWORD,
        data: user,
        method: M_POST
      });

      assertOk(data, 'Reset password failed');
      return data?.status ?? data;
    } catch (err) {
      throw toError(err, 'Reset password failed');
    }
  }

  @Action({ commit: 'mutateHistory', rawError: true })
  async FetchHistory(user: Record<string, any>) {
    try {
      const { data } = await axios({
        url: BASE_URL + USERS_HISTORY,
        data: user,
        method: M_POST
      });

      assertOk(data, 'Fetch history failed');
      return data?.data;
    } catch (err) {
      throw toError(err, 'Fetch history failed');
    }
  }

  @Action({ rawError: true })
  async UpdatePermission(params: Record<string, any>) {
    try {
      const { data } = await axios({
        url: BASE_URL + UPDATE_PERMISSION,
        data: params,
        method: M_POST
      });

      assertOk(data, 'Update permission failed');
      return data?.status;
    } catch (err) {
      throw toError(err, 'Update permission failed');
    }
  }

  @Action({ rawError: true })
  async UpdateCommission(params: Record<string, any>) {
    try {
      const { data } = await axios({
        url: BASE_URL + UPDATE_COMMISSION,
        data: params,
        method: M_PUT
      });

      assertOk(data, 'Update commission failed');
      return data?.status;
    } catch (err) {
      throw toError(err, 'Update commission failed');
    }
  }

  @Action({ rawError: true })
  async CheckPrefix(params: Record<string, any>) {
    try {
      const { data } = await axios({
        url: BASE_URL + CHECK_PREFIX,
        data: params,
        method: M_POST
      });

      assertOk(data, 'Check prefix failed');
      return data?.status;
    } catch (err) {
      throw toError(err, 'Check prefix failed');
    }
  }

  @Action({ rawError: true })
  async FetchPtSubAgent(params: Record<string, any> = {}) {
    try {
      const { data } = await axios({
        url: BASE_URL + GET_PTSUBAGENT,
        data: params,
        method: M_POST
      });

      assertOk(data, 'Fetch PT subagent failed');
      return data?.data;
    } catch (err) {
      throw toError(err, 'Fetch PT subagent failed');
    }
  }

  @Action
  async UpdateShowCurrencySigns(params: Record<string, any> = {}) {
    try {
      const { data } = await axios({
        url: BASE_URL + UPDATE_SHOW_CURRENCY_SIGNS,
        data: params,
        method: M_POST
      });

      assertOk(data, 'Update show currency signs failed');
      return data?.data;
    } catch (err) {
      throw toError(err, 'Update show currency signs failed');
    }
  }

  @Action
  async DownlineDownloadSheet(params: Record<string, any> = {}) {
    try {
      const resp = await axios({
        url: BASE_URL + USERS_SHEET,
        data: params,
        method: M_POST,
        responseType: 'blob'
      } as any);

      const blob = resp?.data as Blob;

      const contentType = String(resp?.headers?.['content-type'] ?? '').toLowerCase();
      if (contentType.includes('application/json')) {
        const text = await blob.text();
        let msg = 'Export failed';
        try {
          const json = JSON.parse(text);
          msg = json?.status?.message || json?.message || json?.msg || msg;
          throw toError({ response: { data: json } }, msg);
        } catch (_e) {
          throw toError({ response: { data: { message: msg } } }, msg);
        }
      }

      return blob;
    } catch (err) {
      throw toError(err, 'Download sheet failed');
    }
  }

  // ===== EDIT AGENT DRAFT =====
  editAgentDraft: any = null;

  @Mutation
  SET_EDIT_AGENT_DRAFT(user: any) {
    this.editAgentDraft = user;
  }

  @Mutation
  CLEAR_EDIT_AGENT_DRAFT() {
    this.editAgentDraft = null;
  }

  @Action
  setEditAgentDraft(user: any) {
    this.context.commit('SET_EDIT_AGENT_DRAFT', user);
  }

  @Action
  clearEditAgentDraft() {
    this.context.commit('CLEAR_EDIT_AGENT_DRAFT');
  }

  get getEditAgentDraft() {
    return this.editAgentDraft;
  }

  // ===== EDIT MEMBER DRAFT =====
  editMemberDraft: any = null;

  @Mutation
  SET_EDIT_MEMBER_DRAFT(user: any) {
    this.editMemberDraft = user;
  }

  @Mutation
  CLEAR_EDIT_MEMBER_DRAFT() {
    this.editMemberDraft = null;
  }

  @Action
  setEditMemberDraft(user: any) {
    this.context.commit('SET_EDIT_MEMBER_DRAFT', user);
  }

  @Action
  clearEditMemberDraft() {
    this.context.commit('CLEAR_EDIT_MEMBER_DRAFT');
  }

  get getEditMemberDraft() {
    return this.editMemberDraft;
  }
}
